import { useMemo } from 'react';
import { useAppStore } from '../../app/hooks/useAppStore';
import { getEvidenceLevelForFamily, getOverallEvidenceLevel, countFamiliesWithEvidence, type EvidenceLevel } from '../../mastery';
export interface UseLabEvidenceResult {
  /** Bewijsniveau voor het kind als geheel, binnen de actieve groep. */
  level: EvidenceLevel;
  /** Snelkoppeling: weten we werkelijk niets? */
  isEmpty: boolean;
  /** Mag het lab een voorstel "voor jou" noemen? */
  mayPersonalize: boolean;
  /** Aantal onderwerpen waarover we genoeg weten — om uit te leggen, niet om te beslissen. */
  familiesWithEvidence: number;
  /** Bewijsniveau voor één onderwerp. */
  levelForFamily: (familyId: string) => EvidenceLevel;
}

/**
 * useLabEvidence — één gedeeld antwoord op "hoeveel weten we over dit kind?".
 *
 * De twee personalisatiepaden in het leerlab (MissionBriefing met eigen
 * LDG-frontier-logica, useLabPersonalization met mastery-aggregaten voor de
 * stationkaarten) wisten niets van elkaar. Daardoor kon het briefing-paneel met
 * gezag spreken terwijl de kaarten ernaast niets konden tonen — bij nul data was
 * dat precies wat er gebeurde.
 *
 * Deze hook voegt die twee paden niet samen; ze hebben verschillende taken. Hij
 * geeft ze dezelfde bron voor de enige vraag die ze allebei fout beantwoordden.
 */
export function useLabEvidence(): UseLabEvidenceResult {
  const {
    state,
    selectedGroup
  } = useAppStore();
  const familyMastery = state.familyMastery;
  const level = useMemo(() => getOverallEvidenceLevel(familyMastery, selectedGroup), [familyMastery, selectedGroup]);
  const familiesWithEvidence = useMemo(() => countFamiliesWithEvidence(familyMastery, selectedGroup), [familyMastery, selectedGroup]);
  return {
    level,
    isEmpty: level === 'none',
    mayPersonalize: level === 'sufficient',
    familiesWithEvidence,
    levelForFamily: (familyId: string) => getEvidenceLevelForFamily(familyMastery, familyId, selectedGroup)
  };
}